import type { DayStatus } from './attendance'

/**
 * เบี้ยขยัน (diligence allowance): money for a month with no absence, no leave and no lateness, more for each such month
 * in a row up to the third, then the third's on. A month is the days a regular payroll run counts time for
 * (`regularWindow`, by the cut-off day), paid in that run. Who gets it, and at which rates, is the employee's
 * `diligence`: living in the company's quarters or outside. A month with any of them gets nothing and the next starts
 * from the first step again. Leave of a kind that keeps it (`keepsDiligence`: annual leave) does not count, nor do
 * leaving early or a missing clock-out. A month someone was not employed all of gets nothing (they start the month
 * after). HR may set the step someone had in a month (`diligenceCarriedMonth`, `diligenceCarriedStep`): carried from
 * before the system, counted on from the month after.
 */

/** `inHouse`: lives in the company's quarters; `outside`: lives outside */
export const diligenceKinds = ['inHouse', 'outside'] as const
export type DiligenceKind = (typeof diligenceKinds)[number]

/** Baht for the first, second and third month in a row (and every one after) */
export const diligenceRates: Record<DiligenceKind, readonly [number, number, number]> = {
  inHouse: [200, 400, 600],
  outside: [400, 800, 1200],
}
export const DILIGENCE_STEPS = 3

/** The amount of a step (0: none) */
export const diligenceAmount = (kind: DiligenceKind | null | undefined, step: number) =>
  (kind && step > 0 && diligenceRates[kind][Math.min(step, DILIGENCE_STEPS) - 1]) || 0

/** One day of a month as the allowance reads it */
export interface DiligenceDay {
  /** Planned to work */
  working: boolean
  status: DayStatus
  lateMinutes: number
  /** Approved leave on a day it counts (a working day, or every day for leave by calendar days): 1 or 0.5 */
  leaveDays: number
  /** Its kind keeps the allowance (annual leave) */
  leaveKeeps: boolean
}

/** What loses a month its allowance: days absent, days of leave, times late */
export interface DiligenceBreaks {
  absent: number
  leave: number
  late: number
}

/** A month's absence, leave (but of kinds that keep it) and lateness; days not over yet have none */
export function diligenceBreaks(days: readonly DiligenceDay[]): DiligenceBreaks {
  const breaks: DiligenceBreaks = { absent: 0, leave: 0, late: 0 }
  for (const day of days) {
    if (day.leaveDays > 0 && !day.leaveKeeps) breaks.leave += day.leaveDays
    if (!day.working) continue
    if (day.status === 'absent') breaks.absent++
    if (day.lateMinutes > 0) breaks.late++
  }
  return breaks
}

/** One month of someone's allowance (worked out by the API, `diligenceOf`) */
export interface DiligenceMonth {
  month: string
  /** 1–3: earned at that step; 0: not this month */
  step: number
  /** By their rates (none: 0) */
  amount: number
  breaks: DiligenceBreaks
  /** Not employed all of its days: nothing, and the month after is their first */
  partial: boolean
  /** Its days are not all over yet: so far */
  counting: boolean
  /** The step HR carried from before the system */
  carried: boolean
  /** Some day of it came to work or was on leave; none: before the system (or never came), earning nothing, shown blank */
  recorded: boolean
}

/** Whether anything of a month is recorded: a day come to work, or on leave */
export const diligenceRecorded = (days: readonly DiligenceDay[]) =>
  days.some((day) => day.status === 'present' || day.status === 'late' || day.status === 'incomplete' || day.status === 'leave')

/** Nothing that loses it */
export const diligenceClean = (breaks: DiligenceBreaks) => breaks.absent === 0 && breaks.leave === 0 && breaks.late === 0

/** A month's step from the one before: one more for a month that earns it, up to the third; none for any other */
export const nextDiligenceStep = (before: number, earned: boolean) => (earned ? Math.min(DILIGENCE_STEPS, before + 1) : 0)
